import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportApi } from '../../api/services';
import {
  TrendingUp, ShoppingBag, Users, DollarSign, XCircle,
  Download, Calendar, Award, MapPin, CreditCard, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { maskKvkkName } from '../../utils/kvkk';

type PeriodType = 'today' | 'week' | 'month' | 'year';

export default function AdminReports() {
  const [period, setPeriod] = useState<PeriodType>('month');

  // Calculate fromDate and toDate based on selected period
  const getDateRange = () => {
    const to = new Date();
    const from = new Date();
    if (period === 'today') {
      from.setHours(0, 0, 0, 0);
    } else if (period === 'week') {
      from.setDate(to.getDate() - 7);
    } else if (period === 'month') {
      from.setDate(to.getDate() - 30);
    } else if (period === 'year') {
      from.setDate(to.getDate() - 365);
    }
    return { fromDate: from.toISOString(), toDate: to.toISOString() };
  };

  const { fromDate, toDate } = getDateRange();
  const queryParams = { fromDate, toDate };

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['report-summary', period],
    queryFn: () => reportApi.getSummary(queryParams).then(r => r.data),
  });

  const { data: dailySales = [], isLoading: dailyLoading, refetch: refetchDaily } = useQuery({
    queryKey: ['report-daily-sales', period],
    queryFn: () => reportApi.getDailySales(queryParams).then(r => r.data),
  });

  const { data: topProducts = [], isLoading: topProductsLoading, refetch: refetchTopProducts } = useQuery({
    queryKey: ['report-top-products', period],
    queryFn: () => reportApi.getTopProducts({ ...queryParams, top: 15 }).then(r => r.data),
  });

  const { data: topCustomers = [], refetch: refetchTopCustomers } = useQuery({
    queryKey: ['report-top-customers', period],
    queryFn: () => reportApi.getTopCustomers({ ...queryParams, top: 10 }).then(r => r.data),
  });

  const { data: neighborhoodSales = [], refetch: refetchNeighborhoods } = useQuery({
    queryKey: ['report-neighborhoods', period],
    queryFn: () => reportApi.getNeighborhoodSales(queryParams).then(r => r.data),
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['report-payment-methods', period],
    queryFn: () => reportApi.getPaymentMethods(queryParams).then(r => r.data),
  });

  const handleRefreshAll = () => {
    refetchSummary();
    refetchDaily();
    refetchTopProducts();
    refetchTopCustomers();
    refetchNeighborhoods();
    toast.success('Raporlar güncellendi');
  };

  const handleExportCsv = async () => {
    try {
      const res = await reportApi.exportDailySalesCsv(queryParams);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `satis-raporu-${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV raporu indirildi');
    } catch {
      toast.error('Rapor indirilemedi');
    }
  };

  const stats = [
    { label: 'Toplam Gelir', value: summary ? `₺${summary.totalRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : '-', icon: DollarSign, color: '#10b981' },
    { label: 'Toplam Sipariş', value: summary?.totalOrders ?? '-', icon: ShoppingBag, color: '#3b82f6' },
    { label: 'Toplam Kayıtlı Üye', value: summary?.totalUsers ?? '-', icon: Users, color: '#8b5cf6' },
    { label: 'Ortalama Sepet', value: summary ? `₺${summary.averageOrderValue?.toFixed(2)}` : '-', icon: TrendingUp, color: '#f59e0b' },
    { label: 'İptal Oranı', value: summary ? `%${summary.cancellationRate}` : '-', icon: XCircle, color: '#ef4444' },
  ];

  const paymentLabels: Record<number | string, string> = {
    1: 'Kapıda Nakit',
    Cash: 'Kapıda Nakit',
    2: 'Kapıda Kredi Kartı (POS)',
    CreditCard: 'Kapıda Kredi Kartı (POS)',
    3: 'Online Kredi Kartı',
    OnlinePayment: 'Online Kredi Kartı',
    OnlineCreditCard: 'Online Kredi Kartı',
    4: 'Metropol',
    Metropol: 'Metropol',
    5: 'Setcard',
    Setcard: 'Setcard',
    6: 'Edenred Ticket',
    EdenredTicket: 'Edenred Ticket',
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header & Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, margin: 0 }}>📊 Raporlar & Satış Analizi</h1>
          <p className="text-muted" style={{ margin: '4px 0 0', fontSize: '0.9rem' }}>
            Günlük, haftalık ve aylık sipariş hareketleri ile ürün satış performansı
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {/* Period selector */}
          <div style={{ display: 'flex', background: 'var(--color-surface)', padding: '4px', borderRadius: '10px', border: '1px solid var(--color-border)' }}>
            <button
              onClick={() => setPeriod('today')}
              className={`btn btn-sm ${period === 'today' ? 'btn-primary' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              Bugün
            </button>
            <button
              onClick={() => setPeriod('week')}
              className={`btn btn-sm ${period === 'week' ? 'btn-primary' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              Bu Hafta (7 Gün)
            </button>
            <button
              onClick={() => setPeriod('month')}
              className={`btn btn-sm ${period === 'month' ? 'btn-primary' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              Bu Ay (30 Gün)
            </button>
            <button
              onClick={() => setPeriod('year')}
              className={`btn btn-sm ${period === 'year' ? 'btn-primary' : ''}`}
              style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            >
              Tüm Yıl
            </button>
          </div>

          <button onClick={handleRefreshAll} className="btn btn-secondary btn-sm" title="Yenile">
            <RefreshCw size={16} />
          </button>

          <button onClick={handleExportCsv} className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Download size={16} /> CSV İndir
          </button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="card" style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${stat.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color, flexShrink: 0 }}>
                <Icon size={24} />
              </div>
              <div>
                <span className="text-muted" style={{ fontSize: '0.8rem', display: 'block', marginBottom: '4px' }}>{stat.label}</span>
                <strong style={{ fontSize: '1.4rem', fontWeight: 800 }}>{summaryLoading ? '...' : stat.value}</strong>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Grid: Gün Gün Satışlar & En Çok Satanlar */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        
        {/* Gün Gün Satışlar Tablosu */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={20} color="var(--color-primary)" />
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>📅 Gün Gün Satış & Sipariş Takibi</h2>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              {dailySales.length} gün kaydedildi
            </span>
          </div>

          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                  <th style={{ padding: '10px 8px' }}>Tarih</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center' }}>Sipariş</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>Toplam Ciro</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>Ort. Sepet</th>
                </tr>
              </thead>
              <tbody>
                {dailyLoading ? (
                  <tr><td colSpan={4} style={{ padding: '24px', textAlign: 'center' }}>Yükleniyor...</td></tr>
                ) : dailySales.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                      Seçilen dönemde henüz sipariş hareketi yok.
                    </td>
                  </tr>
                ) : (
                  dailySales.map((row: any) => (
                    <tr key={row.date} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '12px 8px', fontWeight: 600 }}>
                        {new Date(row.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <span style={{ background: 'var(--color-surface-2)', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                          {row.orderCount}
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--color-primary)' }}>
                        ₺{row.totalRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>
                        ₺{row.averageOrderValue?.toFixed(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* En Çok Satan Ürünler Tablosu */}
        <div className="card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Award size={20} color="#f59e0b" />
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>🏆 En Çok Satan Ürünler</h2>
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
              Top {topProducts.length} ürün
            </span>
          </div>

          <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                  <th style={{ padding: '10px 8px' }}>Sıra & Ürün</th>
                  <th style={{ padding: '10px 8px', textAlign: 'center' }}>Satılan Adet</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right' }}>Toplam Ciro</th>
                </tr>
              </thead>
              <tbody>
                {topProductsLoading ? (
                  <tr><td colSpan={3} style={{ padding: '24px', textAlign: 'center' }}>Yükleniyor...</td></tr>
                ) : topProducts.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                      Seçilen dönemde satış kaydı bulunamadı.
                    </td>
                  </tr>
                ) : (
                  topProducts.map((p: any, idx: number) => (
                    <tr key={p.productId || idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            width: '24px', height: '24px', borderRadius: '50%',
                            background: idx === 0 ? '#ffd700' : idx === 1 ? '#c0c0c0' : idx === 2 ? '#cd7f32' : 'var(--color-surface-2)',
                            color: idx < 3 ? '#000' : 'var(--color-text-secondary)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.75rem', fontWeight: 700
                          }}>
                            {idx + 1}
                          </span>
                          <strong style={{ fontSize: '0.9rem' }}>{p.productName}</strong>
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <span style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>
                          {p.totalQuantity} adet
                        </span>
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--color-success)' }}>
                        ₺{p.totalRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Alt Bölüm: En Çok Sipariş Veren Müşteriler & Mahalle Dağılımı & Ödeme Tipleri */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '24px' }}>
        
        {/* En Çok Harcayan Müşteriler */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Users size={18} color="#8b5cf6" />
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>En Çok Sipariş Veren Müşteriler</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {topCustomers.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>Müşteri verisi yok.</p>
            ) : (
              topCustomers.map((c: any, i: number) => (
                <div key={c.userId || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--color-surface-2)', borderRadius: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{maskKvkkName(c.fullName) || 'İsimsiz Müşteri'}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{c.phone}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: 'var(--color-primary)', fontSize: '0.88rem' }}>₺{c.totalSpent?.toFixed(2)}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{c.orderCount} sipariş</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Mahalle Bazlı Satışlar */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <MapPin size={18} color="var(--color-primary)" />
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Mahalle Bazlı Sipariş Dağılımı</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {neighborhoodSales.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>Mahalle verisi bulunamadı.</p>
            ) : (
              neighborhoodSales.map((n: any, i: number) => (
                <div key={n.neighborhoodName || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--color-surface-2)', borderRadius: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{n.neighborhoodName}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>₺{n.totalRevenue?.toFixed(2)}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-secondary)' }}>{n.orderCount} teslimat</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Ödeme Yöntemleri */}
        <div className="card" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <CreditCard size={18} color="#10b981" />
            <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Ödeme Yöntemi Tercihleri</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {paymentMethods.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>Ödeme verisi bulunamadı.</p>
            ) : (
              paymentMethods.map((pm: any) => (
                <div key={pm.paymentMethod} style={{ padding: '10px 12px', background: 'var(--color-surface-2)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.85rem', fontWeight: 600 }}>
                    <span>{paymentLabels[pm.paymentMethod] || 'Diğer'}</span>
                    <span>%{pm.percentage} ({pm.orderCount} sipariş)</span>
                  </div>
                  <div style={{ height: '6px', background: 'var(--color-border)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${pm.percentage}%`, height: '100%', background: 'var(--color-primary)' }} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
